<template lang="pug">
.page.page-thinking
  rail-layout(wide-main)
    template(#side)
      h1.thinking-title {{ $t('thinking.title') }}
      p.thinking-subtitle {{ $t('thinking.subtitle') }}
      .side-filters
        .section-heading {{ $t('thinking.types.all') }}
        button.filter-row(type="button", :aria-pressed="activeType === 'all'", :class="{ active: activeType === 'all' }", @click="activeType = 'all'")
          span.filter-label {{ $t('thinking.types.all') }}
          span.filter-count {{ items.length }}
        button.filter-row(
          v-for="k in typeOrder",
          :key="'f-' + k", type="button", :aria-pressed="activeType === k",
          :class="{ active: activeType === k }",
          @click="activeType = k"
        )
          type-badge(:type="k")
          span.filter-count {{ counts[k] || 0 }}
      .side-search
        input.search-input(
          type="search",
          v-model.trim="q",
          :placeholder="$t('thinking.searchPlaceholder')",
          :aria-label="$t('thinking.searchPlaceholder')"
        )
        button.clear-search(v-if="q || activeType !== 'all'", type="button", @click="q = ''; activeType = 'all'") {{ isZh ? '清除搜尋與分類' : 'Clear search and filters' }}
        span.search-count(role="status", aria-live="polite")
          span {{ visible.length }}
          span(v-if="q.length >= 2 && ['all', 'dialogue'].includes(activeType)")  · {{ segmentHits.length }} {{ $t('thinking.segments') }}
      .side-links
        router-link.pill(:to="isZh ? '/zh/dialogues/' : '/dialogues/'") {{ $t('thinking.dialoguesColumn') }} →
        router-link.pill(:to="isZh ? '/zh/cv/' : '/cv/'") {{ $t('thinking.allTalks') }}
        a.pill(href="/feed.xml", target="_blank", rel="noopener") Atom


    .thinking-main
      p.intro {{ $t('thinking.intro') }}

      //- Featured (only on the unfiltered, unsearched view)
      section.featured(v-if="featured.length && activeType === 'all' && !q")
        .section-heading {{ $t('thinking.featured') }}
        .featured-row
          template(v-for="it in featured", :key="'f-' + it.id")
            a.featured-card(v-if="it.external", :href="it.href", target="_blank", rel="noopener")
              .card-top
                type-badge(:type="it.type")
                span.card-date {{ it.date }}
              h3.card-title {{ pick(it.title) }}
              p.card-excerpt(v-if="pick(it.excerpt)") {{ pick(it.excerpt) }}
              span.card-source(v-if="pick(it.source)") {{ pick(it.source) }}
            router-link.featured-card(v-else, :to="hrefFor(it)")
              .card-top
                type-badge(:type="it.type")
                span.card-date {{ it.date }}
              h3.card-title {{ pick(it.title) }}
              p.card-excerpt(v-if="pick(it.excerpt)") {{ pick(it.excerpt) }}
              span.card-source(v-if="pick(it.source)") {{ pick(it.source) }}

      //- Hits inside dialogues (per round)
      section.segment-hits(v-if="q && segmentHits.length")
        .section-heading {{ $t('thinking.inDialogues') }}
        ul.hit-list
          li.hit(v-for="h in segmentHits", :key="h.key")
            router-link(:to="(h.lang === 'zh' ? '/zh' : '') + '/dialogues/' + h.slug + '/' + (h.anchor ? '#' + h.anchor : '')")
              span.hit-meta
                type-badge(type="dialogue")
                span {{ h.title }} · {{ h.round ? $t('dialogues.round', { n: h.round }) : $t('dialogues.editedNotes') }} · {{ h.speaker }}
              span.hit-text …{{ h.snippet }}…

      //- Timeline grouped by year
      section.timeline
        template(v-for="group in grouped", :key="group.year")
          h2.year {{ group.year }}
          ul.year-list
            li.item(v-for="it in group.items", :key="it.id")
              a.item-link(v-if="it.external", :href="it.href", target="_blank", rel="noopener")
                .item-meta
                  type-badge(:type="it.type")
                  span.item-date {{ it.date }}
                .item-body
                  h3.item-title {{ pick(it.title) }}
                  p.item-excerpt(v-if="pick(it.excerpt)") {{ pick(it.excerpt) }}
                  .item-foot
                    span.item-source(v-if="pick(it.source)") {{ pick(it.source) }}
                    span.item-ext ↗︎
              router-link.item-link(v-else, :to="hrefFor(it)")
                .item-meta
                  type-badge(:type="it.type")
                  span.item-date {{ it.date }}
                  span.item-no(v-if="it.no") No. {{ it.no }}
                .item-body
                  h3.item-title {{ pick(it.title) }}
                  p.item-excerpt(v-if="pick(it.excerpt)") {{ pick(it.excerpt) }}
                  .item-foot
                    span.item-source(v-if="pick(it.source)") {{ pick(it.source) }}
                    span.item-onsite {{ $t('thinking.onSite') }}
        p.empty(v-if="!grouped.length") {{ $t('thinking.empty') }}
      .side-elsewhere
        .section-heading {{ $t('thinking.elsewhere') }}
        a.pill(href="https://medium.com/@wucheyu", target="_blank", rel="noopener") Medium
        a.pill(href="https://creativecoding.in", target="_blank", rel="noopener") CreativeCoding.in
        a.pill(href="https://www.youtube.com/channel/UCHX_XMhPrtvOmgGOnA2eTmA", target="_blank", rel="noopener") YouTube
        a.pill(:href="educationHref", target="_blank", rel="noopener") TD Workshop
</template>

<script>
import { withSlash } from '@/runtime/site-path.mjs'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { ogTitle } from '@/runtime/page-title.mjs'
import thinking from '@/data/thinking.json'
import { courseHref } from '@/components/teaching/course-link.mjs'
import segments from '@/data/dialogues-search.json'

export default {
  name: 'PageThinking',
  setup() {
    const { t, locale } = useI18n()
    const route = useRoute()
    useHead({
      title: computed(() => t('thinking.title')),
      meta: computed(() => [
        { name: 'description', content: t('thinking.subtitle') },
        { property: 'og:title', content: ogTitle(t('thinking.title'), locale.value) },
        { property: 'og:description', content: t('thinking.subtitle') },
      ]),
      link: computed(() => [{ rel: 'canonical', href: 'https://cheyuwu.com' + (route.path.endsWith('/') ? route.path : route.path + '/') }]),
    })
    return { t, locale }
  },
  data() {
    return {
      activeType: ['dialogue', 'essay', 'talk', 'teaching'].includes(this.$route.query.type) ? this.$route.query.type : 'all',
      q: typeof this.$route.query.q === 'string' ? this.$route.query.q : '',
      typeOrder: ['dialogue', 'essay', 'talk', 'teaching'],
      items: thinking.items,
      counts: thinking.counts,
    }
  },
  computed: {
    isZh() { return this.$route.path.startsWith('/zh') || this.locale === 'zh' },
    currentLang() { return this.isZh ? 'zh' : 'en' },
    // 開課快手連結帶 UTM（2026-10-18 TD 漏斗）：utm_campaign=teaching-thinking
    educationHref() { return courseHref({ slug: 'monolab', platform: 'monolab', url: 'https://education.monolab.world/' }, 'thinking', this.currentLang) },
    visible() {
      const q = this.q.toLowerCase()
      return this.items.filter(it => {
        if (this.activeType !== 'all' && it.type !== this.activeType) return false
        if (!q) return true
        const hay = [this.pick(it.title), this.pick(it.excerpt), this.pick(it.source), (it.tags || []).join(' ')].join(' ').toLowerCase()
        return hay.includes(q)
      })
    },
    segmentHits() {
      const q = this.q.toLowerCase()
      if (q.length < 2 || !['all', 'dialogue'].includes(this.activeType)) return []
      const speakerName = { muse: 'Muse', cheyu: this.isZh ? '哲宇' : 'Che-Yu' }
      const hits = []
      for (const seg of segments) {
        if (seg.lang !== this.currentLang && segments.some(other => other.slug === seg.slug && other.lang === this.currentLang)) continue
        const i = seg.text.toLowerCase().indexOf(q)
        if (i < 0) continue
        const start = Math.max(0, i - 40)
        hits.push({
          key: `${seg.slug}-${seg.lang}-${seg.anchor || seg.round}-${seg.kind}-${i}`,
          slug: seg.slug, round: seg.round, title: seg.title, lang: seg.lang, anchor: seg.anchor || (seg.round ? `round-${seg.round}` : ''),
          speaker: (speakerName[seg.speaker] || seg.speaker) + (seg.kind === 'note' ? (this.isZh ? ' 的筆記' : ' notes') : ''),
          snippet: seg.text.slice(start, i + q.length + 60),
        })
        if (hits.length >= 20) break
      }
      return hits
    },
    featured() { return this.items.filter(it => it.featured).slice(0, 3) },
    grouped() {
      const map = new Map()
      for (const it of this.visible) {
        const y = it.year || '—'
        if (!map.has(y)) map.set(y, [])
        map.get(y).push(it)
      }
      return [...map.entries()].map(([year, items]) => ({ year, items }))
    },
  },
  watch: {
    q() { this.syncSearch() },
    activeType() { this.syncSearch() },
    '$route.query': {
      handler(query) {
        this.q = typeof query.q === 'string' ? query.q : ''
        this.activeType = this.typeOrder.includes(query.type) ? query.type : 'all'
      },
    },
  },
  methods: {
    syncSearch() {
      const query = { ...this.$route.query }
      if (this.q) query.q = this.q; else delete query.q
      if (this.activeType !== 'all') query.type = this.activeType; else delete query.type
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) this.$router.replace({ query })
    },
    pick(value) {
      if (value == null) return ''
      if (typeof value === 'string') return value
      return value[this.currentLang] || value.zh || value.en || ''
    },
    hrefFor(it) {
      if (it.external) return it.href
      if (typeof it.href === 'string') return withSlash(it.href)
      return withSlash(it.href[this.currentLang] || it.href.zh || it.href.en)
    },
  },
}
</script>

<style lang="sass">
.page-thinking
  background-color: #000
  color: #fff
  min-height: 100vh
  text-align: left

  .thinking-title
    font-family: var(--font-display)
    font-size: 2.4rem
    font-weight: 600
    letter-spacing: 0.02em
    margin: 0
  .thinking-subtitle
    font-family: var(--font-display)
    font-size: 1.05rem
    color: #d4d4d0
    line-height: 1.7
    margin: 0

  .side-filters
    border-top: 1px solid var(--line)
    padding-top: 14px
    display: flex
    flex-direction: column
    gap: 4px
  .filter-row
    display: flex
    justify-content: space-between
    align-items: center
    width: 100%
    background: transparent
    border: 0
    border-radius: var(--r-md)
    padding: 7px 12px
    color: var(--ink-2)
    cursor: pointer
    transition: var(--t-hover)
    text-align: left
    &:hover
      background: var(--tint-2)
      color: var(--ink)
    &.active
      background: var(--gold-tint)
      color: var(--ink)
    .filter-count
      font-family: var(--font-display)
      font-size: 1rem
      color: var(--ink)

  .side-search
    display: flex
    flex-direction: column
    gap: 6px
  .search-input
    width: 100%
    background: var(--bg2)
    border: 0
    border-radius: var(--r-pill)
    color: var(--ink)
    padding: 9px 16px
    font-size: 0.92rem
    outline: none
    &:focus
      box-shadow: 0 0 0 1px var(--gold)
    &::placeholder
      color: var(--ink-4)
  .search-count
    font-size: 0.78rem
    color: #777

  .side-links, .side-elsewhere
    display: flex
    flex-wrap: wrap
    gap: 8px
    .section-heading
      width: 100%
      margin-bottom: 2px
  .side-elsewhere
    margin-top: 48px
    border-top: 1px solid var(--line)
    padding-top: 14px

  .thinking-main
    padding-top: 6px

  .intro
    font-size: 1rem
    line-height: 1.85
    color: #9a9a96
    max-width: 720px
    margin: 0 0 30px

  .featured
    margin-bottom: 40px
  .featured-row
    display: grid
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr))
    gap: 14px
  .featured-card
    display: block
    padding: 22px 24px
    border: 0
    border-radius: var(--r-lg)
    background: var(--bg1)
    color: var(--ink)
    text-decoration: none
    transition: var(--t-hover)
    &:hover
      background: var(--bg2)
      text-decoration: none
      .card-title
        color: var(--gold)
    .card-top
      display: flex
      justify-content: space-between
      align-items: center
      margin-bottom: 10px
    .card-date
      font-size: 0.78rem
      color: #777
      letter-spacing: 0.06em
    .card-title
      font-family: var(--font-display)
      font-size: 1.1rem
      font-weight: 600
      line-height: 1.4
      margin-bottom: 8px
    .card-excerpt
      font-size: 0.88rem
      color: #9a9a96
      line-height: 1.65
      margin-bottom: 8px
      display: -webkit-box
      -webkit-line-clamp: 3
      -webkit-box-orient: vertical
      overflow: hidden
    .card-source
      font-size: 0.78rem
      color: #666

  .segment-hits
    margin: 6px 0 22px
    padding: 16px 20px
    border: 0
    border-radius: var(--r-md)
    background: var(--violet-tint)
  .hit-list
    list-style: none
    padding: 0
    margin: 0
    display: grid
    gap: 10px
  .hit a
    display: block
    color: #ddd
    text-decoration: none
    &:hover
      color: #fff
      text-decoration: none
  .hit-meta
    display: flex
    flex-wrap: wrap
    gap: 8px
    align-items: center
    font-size: 0.8rem
    color: #999
    margin-bottom: 4px
  .hit-text
    display: block
    font-size: 0.92rem
    line-height: 1.6
    color: #bbb

  .year
    font-family: var(--font-display)
    font-size: 1.1rem
    color: #777
    letter-spacing: 0.12em
    margin: 40px 0 12px
    padding-bottom: 8px
    border-bottom: 1px solid rgba(255,255,255,0.08)
  .year-list
    list-style: none
    padding: 0
    margin: 0
  .item
    border-bottom: 1px solid rgba(255,255,255,0.05)
  .item-link
    display: grid
    grid-template-columns: 160px 1fr
    gap: 18px
    padding: 18px 6px
    color: #fff
    text-decoration: none
    transition: var(--t-hover)
    border-radius: 4px
    &:hover
      background: rgba(255,255,255,0.025)
      text-decoration: none
      .item-title
        color: var(--gold)
  .item-meta
    display: flex
    flex-direction: column
    gap: 6px
    align-items: flex-start
  .item-date, .item-no
    font-size: 0.78rem
    color: #777
    letter-spacing: 0.06em
  .item-title
    font-size: 1.08rem
    font-weight: 600
    line-height: 1.45
    margin-bottom: 6px
    transition: var(--t-hover)
  .item-excerpt
    font-size: 0.92rem
    color: #9a9a96
    line-height: 1.7
    margin-bottom: 8px
    max-width: 880px
  .item-foot
    display: flex
    gap: 14px
    font-size: 0.78rem
    color: #666
    .item-onsite
      color: #8a8a86
  .empty
    color: #777
    padding: 24px 0

  .clear-search
    align-self: flex-start
    background: transparent
    border: 0
    color: var(--ink-2)
    text-decoration: underline
    padding: 8px 0
    min-height: 44px
    cursor: pointer

  @media (max-width: 991px)
    .side-filters
      flex-direction: row
      flex-wrap: wrap
      gap: 8px
      .section-heading
        display: none
      .filter-row
        width: auto
        min-height: 44px
        gap: 12px
    .side-links .pill
      padding: 9px 14px
    .featured-row
      grid-template-columns: 1fr
    .thinking-title
      font-size: 1.9rem
    .item-link
      grid-template-columns: 1fr
      gap: 8px
    .item-meta
      flex-direction: row
      align-items: center
      gap: 10px
</style>
